/**
 * Wishlist Redux slice
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Product } from '@/types';

interface WishlistItem {
  product: Product;
  addedAt: string;
}

interface WishlistState {
  items: WishlistItem[];
  loadingFromFirebase: boolean;
}

const initialState: WishlistState = {
  items: [],
  loadingFromFirebase: false,
};

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    /**
     * Add item to wishlist
     */
    addToWishlist: (state, action: PayloadAction<Product>) => {
      const exists = state.items.some(
        (item) => item.product.id === action.payload.id
      );

      if (!exists) {
        state.items.push({
          product: action.payload,
          addedAt: new Date().toISOString(),
        });
      }
    },

    /**
     * Remove item from wishlist
     */
    removeFromWishlist: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(
        (item) => item.product.id !== action.payload
      );
    },

    /**
     * Toggle item in wishlist
     */
    toggleWishlist: (state, action: PayloadAction<Product>) => {
      const index = state.items.findIndex(
        (item) => item.product.id === action.payload.id
      );

      if (index !== -1) {
        state.items.splice(index, 1);
      } else {
        state.items.push({
          product: action.payload,
          addedAt: new Date().toISOString(),
        });
      }
    },

    /**
     * Clear wishlist
     */
    clearWishlist: (state) => {
      state.items = [];
    },

    /**
     * Replace all wishlist items (used after Firebase load/merge)
     */
    setWishlistItems: (state, action: PayloadAction<WishlistItem[]>) => {
      state.items = action.payload;
    },

    setWishlistLoading: (state, action: PayloadAction<boolean>) => {
      state.loadingFromFirebase = action.payload;
    },
  },
});

// Export actions
export const {
  addToWishlist,
  removeFromWishlist,
  toggleWishlist,
  clearWishlist,
  setWishlistItems,
  setWishlistLoading,
} = wishlistSlice.actions;

// Selectors
export const selectWishlistItems = (state: { wishlist: WishlistState }) =>
  state.wishlist.items;

export const selectWishlistCount = (state: { wishlist: WishlistState }) =>
  state.wishlist.items.length;

export const selectIsInWishlist = (productId: string) => (state: { wishlist: WishlistState }) =>
  state.wishlist.items.some((item) => item.product.id === productId);

export const selectWishlistLoading = (state: { wishlist: WishlistState }) =>
  state.wishlist.loadingFromFirebase;

export default wishlistSlice.reducer;
